// client/src/components/ProjectMembersPanel.js
// Self-contained panel for managing project-level members (clients).
// Drop this into ProjectScorecard or any project-level page.
//
// Usage:
//   <ProjectMembersPanel projectId={projectId} apiBaseUrl={apiBaseUrl} isAdmin={isAdmin} />
//
// Props:
//   projectId  — numeric project id (from useParams or passed down)
//   apiBaseUrl — e.g. process.env.REACT_APP_API_URL + '/api'
//   isAdmin    — boolean; controls whether invite/remove actions are shown
// UPDATED Jun 2026: Initial build for project-scoped client access.

import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';

const colors = {
  primary:   '#39393a',
  teal:      '#048ba8',
  coral:     '#e76f51',
  green:     '#01796f',
  textMuted: '#6b7280',
  border:    '#e5e7eb',
  bgSubtle:  '#f9fafb',
};

const ROLE_COLORS = {
  viewer: { bg: '#F1EFE8', text: '#444441' },
  editor: { bg: '#E1F5EE', text: '#085041' },
};

const STATUS_COLORS = {
  active:  { bg: '#EAF3DE', text: '#3B6D11' },
  invited: { bg: '#FAEEDA', text: '#854F0B' },
};

// ── Primitives ────────────────────────────────────────────────────────────────

const SectionHeader = ({ children }) => (
  <div style={{
    padding: '14px 20px', borderBottom: `1px solid ${colors.border}`,
    fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em',
    textTransform: 'uppercase', color: colors.textMuted,
    backgroundColor: colors.bgSubtle
  }}>
    {children}
  </div>
);

const Badge = ({ label, c }) => (
  <span style={{
    fontSize: '11px', fontWeight: '600', padding: '2px 8px',
    borderRadius: '4px', backgroundColor: c.bg, color: c.text
  }}>
    {label}
  </span>
);

const TealButton = ({ children, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: '8px 16px', fontSize: '13px', fontWeight: '500',
      color: disabled ? colors.textMuted : 'white',
      backgroundColor: disabled ? colors.border : colors.teal,
      border: 'none', borderRadius: '6px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s',
      fontFamily: "'Work Sans', sans-serif"
    }}
  >
    {children}
  </button>
);

const GhostButton = ({ children, onClick, danger = false }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '4px 10px', fontSize: '12px', fontWeight: '500',
        color: danger
          ? (hovered ? '#991b1b' : colors.coral)
          : (hovered ? colors.primary : colors.textMuted),
        backgroundColor: hovered ? (danger ? '#fef2f2' : colors.bgSubtle) : 'transparent',
        border: `1px solid ${danger ? (hovered ? '#fca5a5' : '#fcd4c8') : colors.border}`,
        borderRadius: '5px', cursor: 'pointer',
        transition: 'all 0.15s',
        fontFamily: "'Work Sans', sans-serif"
      }}
    >
      {children}
    </button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

function ProjectMembersPanel({ projectId, apiBaseUrl, isAdmin }) {
  const { user } = useUser();
  const clerkUserId = user?.id;

  const [members,    setMembers]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole,  setInviteRole]  = useState('viewer');
  const [inviting,   setInviting]   = useState(false);
  const [msg,        setMsg]        = useState(null);

  const flash = (text, error = false) => {
    setMsg({ text, error });
    setTimeout(() => setMsg(null), 3500);
  };

  const loadMembers = useCallback(async () => {
    if (!projectId || !clerkUserId) return;
    setLoading(true);
    try {
      const res  = await fetch(`${apiBaseUrl}/projects/${projectId}/members`, {
        headers: { 'x-user-id': clerkUserId }
      });
      const data = await res.json();
      if (res.ok) setMembers(data.members || []);
    } catch (err) {
      console.error('[ProjectMembersPanel] load error', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, clerkUserId, apiBaseUrl]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res  = await fetch(`${apiBaseUrl}/projects/${projectId}/members/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': clerkUserId },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.detail);
      setInviteEmail('');
      flash(`Invite sent to ${inviteEmail.trim()}`);
      await loadMembers();
    } catch (err) {
      flash(err.message, true);
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (memberId, newRole) => {
    try {
      const res = await fetch(`${apiBaseUrl}/projects/${projectId}/members/${memberId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-user-id': clerkUserId },
        body: JSON.stringify({ role: newRole })
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: newRole } : m));
    } catch (err) {
      alert(`Could not change role: ${err.message}`);
    }
  };

  const removeMember = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from this project?`)) return;
    try {
      const res = await fetch(`${apiBaseUrl}/projects/${projectId}/members/${memberId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': clerkUserId }
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setMembers(prev => prev.filter(m => m.id !== memberId));
    } catch (err) {
      alert(`Could not remove: ${err.message}`);
    }
  };

  const activeMembers  = members.filter(m => m.status === 'active');
  const invitedMembers = members.filter(m => m.status === 'invited');

  return (
    <div style={{
      backgroundColor: 'white',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      overflow: 'hidden',
      fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>

      {/* Active members */}
      <SectionHeader>
        Project access · {activeMembers.length} active
        {invitedMembers.length > 0 && ` · ${invitedMembers.length} pending`}
      </SectionHeader>

      {loading ? (
        <div style={{ padding: '20px', fontSize: '13px', color: colors.textMuted }}>Loading…</div>
      ) : members.filter(m => m.status !== 'removed').length === 0 ? (
        <div style={{ padding: '20px', fontSize: '13px', color: colors.textMuted, fontStyle: 'italic' }}>
          No external members yet. Invite a client below.
        </div>
      ) : (
        members.filter(m => m.status !== 'removed').map((m, i, arr) => (
          <div
            key={m.id}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '11px 20px',
              borderBottom: i === arr.length - 1 ? 'none' : `1px solid ${colors.border}`
            }}
          >
            {/* Avatar initial */}
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              backgroundColor: m.status === 'invited' ? '#FAEEDA' : '#E6F1FB',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', fontWeight: '600',
              color: m.status === 'invited' ? '#854F0B' : '#0C447C',
              flexShrink: 0
            }}>
              {(m.email?.[0] || '?').toUpperCase()}
            </div>

            {/* Email + date */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: '500', color: colors.primary }}>
                {m.email}
              </div>
              <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '1px' }}>
                {m.status === 'invited'
                  ? `Invited ${new Date(m.invited_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                  : m.joined_at
                    ? `Joined ${new Date(m.joined_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                    : ''
                }
              </div>
            </div>

            {/* Role — editable for admins on active members */}
            {isAdmin && m.status === 'active' ? (
              <select
                value={m.role}
                onChange={e => changeRole(m.id, e.target.value)}
                style={{
                  fontSize: '12px', padding: '4px 8px', borderRadius: '5px',
                  border: `1px solid ${colors.border}`, backgroundColor: 'white',
                  cursor: 'pointer', fontFamily: "'Work Sans', sans-serif"
                }}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </select>
            ) : (
              <Badge
                label={m.role.charAt(0).toUpperCase() + m.role.slice(1)}
                c={ROLE_COLORS[m.role] || ROLE_COLORS.viewer}
              />
            )}

            {/* Status badge for pending */}
            {m.status === 'invited' && (
              <Badge label="Pending" c={STATUS_COLORS.invited} />
            )}

            {/* Remove */}
            {isAdmin && (
              <GhostButton danger onClick={() => removeMember(m.id, m.email)}>
                {m.status === 'invited' ? 'Cancel' : 'Remove'}
              </GhostButton>
            )}
          </div>
        ))
      )}

      {/* Invite form — admin only */}
      {isAdmin && (
        <div style={{ borderTop: `1px solid ${colors.border}`, padding: '16px 20px' }}>
          <div style={{
            fontSize: '11px', fontWeight: '600', color: colors.textMuted,
            letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '10px'
          }}>
            Invite a client to this project
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="email"
              placeholder="client@example.com"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') sendInvite(); }}
              style={{
                flex: 1, minWidth: '180px', fontSize: '13px',
                padding: '7px 12px', borderRadius: '6px',
                border: `1px solid ${colors.border}`, outline: 'none',
                fontFamily: "'Work Sans', sans-serif"
              }}
            />
            <select
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value)}
              style={{
                fontSize: '13px', padding: '7px 10px', borderRadius: '6px',
                border: `1px solid ${colors.border}`, backgroundColor: 'white',
                fontFamily: "'Work Sans', sans-serif", cursor: 'pointer'
              }}
            >
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
            </select>
            <TealButton onClick={sendInvite} disabled={inviting || !inviteEmail.trim()}>
              {inviting ? 'Sending…' : 'Invite'}
            </TealButton>
          </div>

          {msg && (
            <div style={{
              marginTop: '10px', padding: '8px 12px', borderRadius: '6px',
              fontSize: '13px',
              color: msg.error ? '#991b1b' : colors.green,
              backgroundColor: msg.error ? '#fef2f2' : '#EAF3DE',
              border: `1px solid ${msg.error ? '#fca5a5' : '#C0DD97'}`
            }}>
              {msg.text}
            </div>
          )}

          <div style={{ marginTop: '10px', fontSize: '12px', color: colors.textMuted }}>
            Viewers can see dashboards and reports for this project only. They cannot see other projects or edit anything.
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectMembersPanel;
